export interface Chain {
  id: string;
  name: string;
  type: 'restaurant' | 'delivery' | 'gift-card';
  description: string;
  dealStatus: 'active' | 'limited' | 'varies';
  dealTitle?: string;
  dealDetail?: string;
  dealHours?: string;
  dealMaxDiscount?: string;
  discount?: string;
  requiredApp: string[];
  paymentMethods: string[];
  acceptedTokens?: string;
  locations?: string;
  since?: string;
  website: string;
  logoColor: string;
}

export const paymentMethods = [
  { key: 'all', label: 'All Methods' },
  { key: 'flexa', label: 'Flexa' },
  { key: 'fold', label: 'Fold' },
  { key: 'bakkt', label: 'Bakkt' },
  { key: 'bitpay', label: 'BitPay' },
  { key: 'lightning', label: 'Lightning' },
  { key: 'bitcoin', label: 'Bitcoin Direct' },
  { key: 'coinsbee', label: 'CoinsBee' },
  { key: 'gyft', label: 'Gyft' },
];

export const chains: Chain[] = [
  {
    id: 'chain-sheetz',
    name: 'Sheetz',
    type: 'restaurant',
    description:
      "Major U.S. convenience chain with 750+ locations accepting crypto via Flexa. Home of the 'Crypto Crave & Save' daily deal — 50% off every afternoon.",
    dealStatus: 'active',
    dealTitle: 'Crypto Crave & Save',
    dealDetail: '50% off your purchase when you pay with crypto via Flexa',
    dealHours: '3:00 PM – 7:00 PM daily',
    dealMaxDiscount: 'Max $15 off · Up to 10 transactions/account',
    discount: '50% OFF',
    requiredApp: ['SPEDN', 'Zashi', 'Nighthawk Wallet', 'Nexus Wallet'],
    paymentMethods: ['flexa', 'bitcoin'],
    acceptedTokens: 'Bitcoin, Ethereum, stablecoins + more via Flexa',
    locations: '750+ US locations',
    since: '2021',
    website: 'https://www.sheetz.com',
    logoColor: '#E63946',
  },
  {
    id: 'chain-steaknshake',
    name: "Steak 'n Shake",
    type: 'restaurant',
    description:
      'First major U.S. restaurant chain to accept Bitcoin at all 397 locations via Lightning Network. In-store, drive-thru, and kiosk. Cuts payment processing fees ~50%.',
    dealStatus: 'active',
    dealTitle: '$5 Bitcoin Back via Fold',
    dealDetail: 'Order a Bitcoin Meal or Bitcoin Steakburger, upload receipt at bitcoinmealdeal.com, get $5 BTC via Fold app',
    requiredApp: ['Fold App', 'Lightning Wallet'],
    paymentMethods: ['lightning', 'bitcoin', 'fold'],
    acceptedTokens: 'Bitcoin (Lightning Network)',
    locations: '397 US locations',
    since: '2024',
    website: 'https://www.steaknshake.com',
    logoColor: '#C0392B',
  },
  {
    id: 'chain-chipotle',
    name: 'Chipotle',
    type: 'restaurant',
    description:
      'All 2,950+ US Chipotle locations accept crypto payments via Flexa. Supports 98 cryptocurrencies including Bitcoin, Ether, and 7 USDC stablecoins. Payment completes in under a second.',
    dealStatus: 'active',
    dealTitle: '98 Tokens Accepted',
    dealDetail: 'Open a Flexa-enabled app, tap Chipotle, scan at checkout — instant crypto-to-fiat conversion',
    requiredApp: ['Flexa-enabled app'],
    paymentMethods: ['flexa', 'bitcoin'],
    acceptedTokens: '98 cryptocurrencies including BTC, ETH, USDC',
    locations: '2,950+ US locations',
    since: '2022',
    website: 'https://www.chipotle.com',
    logoColor: '#A52A2A',
  },
  {
    id: 'chain-subway',
    name: 'Subway',
    type: 'restaurant',
    description:
      'One of the earliest fast food adopters — trialed Bitcoin payments at select franchise locations since 2013. Availability varies by franchise owner.',
    dealStatus: 'varies',
    dealTitle: 'Bitcoin at Select Locations',
    dealDetail: 'Availability depends on individual franchise owner — call ahead to confirm',
    requiredApp: ['Bitcoin Wallet'],
    paymentMethods: ['bitcoin'],
    acceptedTokens: 'Bitcoin (select locations)',
    locations: 'Select US franchise locations',
    since: '2013',
    website: 'https://www.subway.com',
    logoColor: '#009B48',
  },
  {
    id: 'chain-quiznos',
    name: "Quiznos",
    type: 'restaurant',
    description:
      'Accepts Bitcoin via Bakkt at select locations. Bakkt converts crypto to fiat before reaching the register — no crypto volatility risk for the merchant.',
    dealStatus: 'limited',
    dealTitle: 'Bitcoin via Bakkt',
    dealDetail: 'Crypto-to-fiat conversion at point of sale via Bakkt app. Select locations only.',
    requiredApp: ['Bakkt App'],
    paymentMethods: ['bakkt', 'bitcoin'],
    acceptedTokens: 'Bitcoin (via Bakkt conversion)',
    locations: 'Select US locations',
    since: '2020',
    website: 'https://www.quiznos.com',
    logoColor: '#D4500A',
  },
  {
    id: 'chain-doordash-ubereats',
    name: 'DoorDash & Uber Eats',
    type: 'delivery',
    description:
      'Access both delivery giants with crypto by purchasing gift cards via BitPay or Moon browser extension. Convert your crypto holdings into usable gift card balances for any food delivery order.',
    dealStatus: 'active',
    dealTitle: 'Crypto Gift Cards via BitPay & Moon',
    dealDetail: 'Buy DoorDash or Uber Eats gift cards with Bitcoin via BitPay app or Moon browser extension',
    requiredApp: ['BitPay App', 'Moon Extension'],
    paymentMethods: ['bitpay', 'bitcoin'],
    acceptedTokens: 'Bitcoin, Ethereum, and others via BitPay/Moon',
    locations: 'Nationwide (US)',
    website: 'https://www.bitpay.com/gift-cards/',
    logoColor: '#FF3008',
  },
  {
    id: 'chain-coinsbee',
    name: 'CoinsBee',
    type: 'gift-card',
    description:
      "Gift card platform supporting McDonald's, Uber Eats, DoorDash, Deliveroo, HelloFresh, and more. Pay with Bitcoin, Ethereum, Litecoin, XRP, Nano, and other cryptocurrencies.",
    dealStatus: 'active',
    dealTitle: 'McDonald\'s + 50+ Food Brands',
    dealDetail: "Browse McDonald's, Uber Eats, DoorDash, Deliveroo, HelloFresh gift cards — pay with BTC and 20+ cryptos",
    requiredApp: ['CoinsBee Account'],
    paymentMethods: ['coinsbee', 'bitcoin'],
    acceptedTokens: 'BTC, ETH, LTC, XRP, Nano + 20 more',
    locations: 'Online — global delivery',
    website: 'https://www.coinsbee.com',
    logoColor: '#F7931A',
  },
  {
    id: 'chain-gyft',
    name: 'Gyft',
    type: 'gift-card',
    description:
      "Purchase gift cards for 200+ brands including Domino's, Pizza Hut, Chipotle, and more with Bitcoin. One of the longest-running crypto gift card platforms.",
    dealStatus: 'active',
    dealTitle: '200+ Brand Gift Cards with Bitcoin',
    dealDetail: "Redeem Bitcoin for Domino's, Pizza Hut, Chipotle, and 200+ other restaurant and delivery gift cards",
    requiredApp: ['Gyft Account'],
    paymentMethods: ['gyft', 'bitcoin'],
    acceptedTokens: 'Bitcoin',
    locations: 'Online — US',
    website: 'https://www.gyft.com',
    logoColor: '#6C3483',
  },
  {
    id: 'chain-bitrefill',
    name: 'Bitrefill',
    type: 'gift-card',
    description:
      "Top-up gift cards for fast food and delivery brands worldwide using Bitcoin and Lightning Network. Known for instant delivery and broad international coverage.",
    dealStatus: 'active',
    dealTitle: 'Fast Food Gift Cards via Lightning',
    dealDetail: 'Buy gift cards for Starbucks, Domino\'s, Subway, and delivery platforms with Bitcoin or Lightning',
    requiredApp: ['Bitrefill Account', 'Bitcoin Wallet'],
    paymentMethods: ['lightning', 'bitcoin'],
    acceptedTokens: 'Bitcoin, Lightning, ETH, USDT',
    locations: 'Online — global delivery',
    website: 'https://www.bitrefill.com',
    logoColor: '#FF6600',
  },
];